 <script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "#imports";
import { useAuthBootstrap } from "~/composables/useAuthBootstrap";

const route = useRoute();
const { isAuthorized, isBootstrapping, hasCheckedBootstrap } =
  useAuthBootstrap();
const normalizedPath = computed(() =>
  route.path === "/" ? "/" : route.path.replace(/\/+$/, "") || "/",
);
const isPublicAuthPath = computed(
  () =>
    normalizedPath.value === "/login" ||
    normalizedPath.value === "/register" ||
    normalizedPath.value === "/info",
);

const showAuthLoader = computed(
  () =>
    !isPublicAuthPath.value &&
    (!hasCheckedBootstrap.value || isBootstrapping.value),
);
</script>

<template>
  <div>
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <div
      v-if="showAuthLoader"
      class="auth-loader"
      role="status"
      aria-live="polite"
      aria-label="Проверка авторизации"
    >
      <div class="auth-loader__halo auth-loader__halo--one" />
      <div class="auth-loader__halo auth-loader__halo--two" />

      <div class="auth-loader__content">
        <div class="auth-loader__logo-wrap">
          <img src="/logo.webp" alt="Логотип" class="auth-loader__logo" />
          <span class="auth-loader__ring" />
        </div>

        <p class="auth-loader__title">Вход</p>
        <p class="auth-loader__subtitle">
          Проверяем сессию
          <span class="auth-loader__dots" aria-hidden="true">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </p>

        <div class="auth-loader__bar">
          <span />
        </div>
      </div>
    </div>

    <PostLoginNewsOverlay
      v-if="!isPublicAuthPath && hasCheckedBootstrap && isAuthorized"
    />
  </div>
</template>

<style scoped>
.auth-loader {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background:
    radial-gradient(
      circle at 20% 20%,
      rgb(var(--color-orange-rgb) / 0.14),
      transparent 45%
    ),
    radial-gradient(
      circle at 80% 78%,
      rgb(var(--color-gray-dark-rgb) / 0.08),
      transparent 42%
    ),
    linear-gradient(
      145deg,
      var(--color-surface-base),
      var(--color-surface-elevated)
    );
}

.auth-loader__halo {
  position: absolute;
  border-radius: 9999px;
  filter: blur(64px);
  pointer-events: none;
}

.auth-loader__halo--one {
  width: 340px;
  height: 340px;
  background: rgb(var(--color-orange-rgb) / 0.18);
  top: 18%;
  left: 8%;
  animation: loader-halo 6.5s ease-in-out infinite;
}

.auth-loader__halo--two {
  width: 280px;
  height: 280px;
  background: rgb(var(--color-gray-dark-rgb) / 0.12);
  right: 10%;
  bottom: 15%;
  animation: loader-halo 7.4s ease-in-out infinite reverse;
}

.auth-loader__content {
  position: relative;
  width: min(92vw, 380px);
  border-radius: 24px;
  padding: 34px 28px 30px;
  background: rgb(var(--color-white-rgb) / 0.86);
  box-shadow: var(--shadow-auth-panel);
  backdrop-filter: blur(6px);
  text-align: center;
}

.auth-loader__logo-wrap {
  position: relative;
  width: 132px;
  height: 132px;
  margin: 0 auto 18px;
  border-radius: 9999px;
  display: grid;
  place-items: center;
  background: var(--color-white);
  box-shadow: var(--shadow-auth-logo);
}

.auth-loader__logo {
  width: 84px;
  height: auto;
  object-fit: contain;
  animation: loader-bob 2.1s ease-in-out infinite;
}

.auth-loader__ring {
  position: absolute;
  inset: -9px;
  border: 3px solid rgb(var(--color-orange-rgb) / 0.2);
  border-top-color: rgb(var(--color-orange-rgb) / 0.85);
  border-radius: 9999px;
  animation: loader-spin 1.25s linear infinite;
}

.auth-loader__title {
  margin: 0;
  font-size: 34px;
  line-height: 1;
  letter-spacing: 0.01em;
  color: var(--color-gray-title);
  font-family: var(--font-helvetica);
  font-weight: 700;
}

.auth-loader__subtitle {
  margin: 12px 0 16px;
  font-size: 15px;
  color: var(--color-gray-subtle);
  font-family: var(--font-helvetica);
}

.auth-loader__dots {
  display: inline-flex;
  min-width: 20px;
  justify-content: flex-start;
}

.auth-loader__dots span {
  opacity: 0.2;
  animation: loader-dots 1.2s infinite;
}

.auth-loader__dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.auth-loader__dots span:nth-child(3) {
  animation-delay: 0.4s;
}

.auth-loader__bar {
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 9999px;
  background: rgb(var(--color-gray-dark-rgb) / 0.08);
  overflow: hidden;
}

.auth-loader__bar span {
  position: absolute;
  inset: 0;
  width: 38%;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    var(--color-orange),
    var(--color-selected-orange)
  );
  animation: loader-progress 1.4s ease-in-out infinite;
}

@keyframes loader-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes loader-bob {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-3px) scale(1.02);
  }
}

@keyframes loader-halo {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(18px, -14px, 0) scale(1.08);
  }
}

@keyframes loader-progress {
  0% {
    transform: translateX(-110%);
  }
  100% {
    transform: translateX(300%);
  }
}

@keyframes loader-dots {
  0%,
  20%,
  100% {
    opacity: 0.2;
  }
  50% {
    opacity: 1;
  }
}

@media (max-width: 640px) {
  .auth-loader__content {
    width: min(92vw, 330px);
    padding: 28px 20px 24px;
    border-radius: 20px;
  }

  .auth-loader__logo-wrap {
    width: 112px;
    height: 112px;
    margin-bottom: 14px;
  }

  .auth-loader__logo {
    width: 72px;
  }

  .auth-loader__title {
    font-size: 28px;
  }
}
</style>
